import { DetailedAccount } from '../../models/crm/types';
import { NullableString } from '../../util/types';
import { getCurrentAccount } from '../crm/helpers';
import { RootState } from '../index';
import { hasValidCookie } from '../selectors';

import { AccountFlowState } from './index';
import { AccountUsability, hasAnyUsableAccount, isAccountUsable, isExpirationPassed } from './usability';

export type DesiredResources = {
  tradingSocket: boolean;
  usableAccountIds: number[];
  closeoutStream: NullableString;
  closeoutStatus: NullableString;
};

export const NO_RESOURCES: DesiredResources = {
  tradingSocket: false,
  usableAccountIds: [],
  closeoutStream: null,
  closeoutStatus: null,
};

const isFinished = (
  { finishedAccountReference, finishedReason }: AccountFlowState,
  referenceId: string,
): boolean => finishedReason !== null && finishedAccountReference === referenceId;

const toUsability = (account: DetailedAccount, accountFlow: AccountFlowState): AccountUsability => ({
  account,
  isExpired: isExpirationPassed(account.account_expiration_date),
  isFinished: isFinished(accountFlow, account.reference_id),
});

export const getDesiredResources = (state: RootState): DesiredResources => {
  const isReady = hasValidCookie(state) && state.app.loaded;

  if (!isReady) {
    return NO_RESOURCES;
  }

  const currentAccountReference = getCurrentAccount(state);
  const accounts = (state.crm.detailedAccounts.tradingAccounts ?? [])
    .map(account => toUsability(account, state.accountFlow));

  const usableAccounts = accounts.filter(isAccountUsable);
  const current = accounts.find(({ account }) => account.reference_id === currentAccountReference);
  const isCurrentUsable = !!current && isAccountUsable(current);

  return {
    tradingSocket: hasAnyUsableAccount(accounts),
    usableAccountIds: usableAccounts.map(({ account }) => account.account_id),
    closeoutStream: isCurrentUsable ? currentAccountReference : null,
    closeoutStatus: isCurrentUsable ? currentAccountReference : null,
  };
};

export const needsSocketCycle = (connectedAccountIds: number[], usableAccountIds: number[]): boolean =>
  usableAccountIds.some(accountId => !connectedAccountIds.includes(accountId));

export const isSameDesiredResources = (left: DesiredResources, right: DesiredResources): boolean => (
  left.tradingSocket === right.tradingSocket
  && left.closeoutStream === right.closeoutStream
  && left.closeoutStatus === right.closeoutStatus
  && left.usableAccountIds.length === right.usableAccountIds.length
  && left.usableAccountIds.every((accountId, index) => accountId === right.usableAccountIds[index])
);
